import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { getImageUrl } from '../../utils/api';
import Loader from '../../components/common/Loader';
import BadgeStatus from '../../components/common/BadgeStatus';
import '../../styles/Forms.css';
import '../../styles/Table.css';
import { can, PERMISSIONS, getPeopleBasePath } from '../../utils/permissions';

const PersonDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const peopleBase = getPeopleBasePath(location.pathname);
  const sessionUser = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  })();
  const isOwnProfile =
    String(sessionUser?.id || sessionUser?._id) === String(id);
  const showEditButton = can(PERMISSIONS.EDIT_EMPLOYEE) || isOwnProfile;
  const [loading, setLoading] = useState(false);
  const [person, setPerson] = useState(null);
  const [assignedAssets, setAssignedAssets] = useState([]);
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetchPersonData();
    fetchSitesAndDepartments();
  }, [id]);

  const fetchPersonData = async () => {
    try {
      setLoading(true);
      
      // Fetch person details
      const personResponse = await api.getUser(id);
      if (personResponse.success) {
        setPerson(personResponse.data);
        
        // Fetch assigned assets
        const assetsResponse = await api.getUserAssets(id);
        if (assetsResponse.success) {
          setAssignedAssets(assetsResponse.data || []);
        }
      } else {
        toast.error('Person not found');
        navigate(peopleBase);
      }
    } catch (error) {
      console.error('Error fetching person details:', error);
      toast.error('Error loading person details');
      navigate(peopleBase);
    } finally {
      setLoading(false);
    }
  };

  const fetchSitesAndDepartments = async () => {
    try {
      const sitesRes = await api.getSites({ isActive: 'true' });
      if (sitesRes.success) {
        setSites(sitesRes.data || []);
      }

      const deptsRes = await api.getDepartments({ isActive: 'true' });
      if (deptsRes.success) {
        setDepartments(deptsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching sites and departments:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return <Loader />;
  }

  if (!person) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: '#718096', fontSize: '14px' }}>Person not found</p>
        <button
          onClick={() => navigate(peopleBase)}
          style={{
            marginTop: '16px',
            padding: '8px 16px',
            background: 'linear-gradient(rgb(26, 32, 44) 0%, rgb(45, 55, 72) 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '7px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '12px',
          }}
        >
          Back to People
        </button>
      </div>
    );
  }

  return (
    <div className="person-detail-page" style={{ padding: '16px' }}>
      {/* Header with Actions */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div>
          <button
            onClick={() => navigate(peopleBase)}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'linear-gradient(rgb(26, 32, 44) 0%, rgb(45, 55, 72) 100%)',
              color: 'white',
              borderRadius: '7px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '12px',
              marginRight: '12px',
            }}
          >
            ← Back
          </button>
        </div>
        {showEditButton && (
        <button
          onClick={() => navigate(`${peopleBase}/${id}/edit`)}
          className="btn-primary"
          style={{ padding: '8px 16px', fontSize: '13px' }}
        >
          Edit
        </button>
        )}
      </div>

      {/* Profile Information */}
      <div className="dashboard-card" style={{ marginBottom: '16px' }}>
        <div className="card-header" style={{ padding: '12px 16px' }}>
          <h2 className="card-title" style={{ margin: 0, fontSize: '16px' }}>Personal Information</h2>
        </div>
        <div className="card-body" style={{ padding: '16px' }}>
          <div className="responsive-grid-2" style={{ gap: '16px', marginBottom: '12px' }}>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Full Name</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {person.name || 'N/A'}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Email Address</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {person.email || 'N/A'}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '0' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Phone Number</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {person.phone || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Details */}
      <div className="dashboard-card" style={{ marginBottom: '16px' }}>
        <div className="card-header" style={{ padding: '12px 16px' }}>
          <h2 className="card-title" style={{ margin: 0, fontSize: '16px' }}>Profile Information</h2>
        </div>
        <div className="card-body" style={{ padding: '16px' }}>
          <div className="responsive-grid-3" style={{ gap: '12px', marginBottom: '12px' }}>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Employee ID</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {person.employeeId || 'N/A'}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Job Title</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {person.title || 'N/A'}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Location</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {person.location || 'N/A'}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Site</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {person.site?.name || sites.find(s => (s._id || s.id) === (person.site?._id || person.site))?.name || 'N/A'}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Department</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {person.department?.name || departments.find(d => (d._id || d.id) === (person.department?._id || person.department))?.name || 'N/A'}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Manager</label>
              <div style={{
                padding: '8px 12px',
                background: '#f7fafc',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {person.manager?.name || 'N/A'}
              </div>
            </div>
          </div>

          {person.notes && (
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Notes</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                whiteSpace: 'pre-wrap'
              }}>
                {person.notes}
              </div>
            </div>
          )}

          {/* Account Info */}
          <div style={{
            padding: '12px',
            background: '#f7fafc',
            borderRadius: '6px',
            marginTop: '12px'
          }}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#4a5568' }}>
              Account Information
            </h3>
            <div className="responsive-grid-3" style={{ gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '2px' }}>
                  Role
                </label>
                <div style={{ fontSize: '13px', color: '#1a202c', fontWeight: 500 }}>
                  {person.role ? person.role.charAt(0).toUpperCase() + person.role.slice(1) : 'N/A'}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '2px' }}>
                  Account Created
                </label>
                <div style={{ fontSize: '13px', color: '#1a202c', fontWeight: 500 }}>
                  {formatDate(person.createdAt)}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '2px' }}>
                  Account Status
                </label>
                <div style={{ fontSize: '13px', color: '#1a202c', fontWeight: 500 }}>
                  <span style={{
                    padding: '3px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 600,
                    backgroundColor: person.isActive ? '#d1fae5' : '#fee2e2',
                    color: person.isActive ? '#065f46' : '#991b1b'
                  }}>
                    {person.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Assets */}
      <div className="dashboard-card">
        <div className="card-header" style={{ padding: '12px 16px' }}>
          <h2 className="card-title" style={{ margin: 0, fontSize: '16px' }}>
            Assigned Assets ({assignedAssets.length})
          </h2>
        </div>
        <div className="card-body" style={{ padding: '16px' }}>
          {assignedAssets.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📦</div>
              <p style={{ margin: 0, fontSize: '14px' }}>No assets assigned to this person</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0', background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)' }}>
                    <th style={{ textAlign: 'left', padding: '12px', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Image</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tag ID</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Brand</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Assigned Date</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontSize: '11px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedAssets.map((asset) => {
                    const assetId = asset.id || asset._id;
                    return (
                      <tr 
                        key={assetId}
                        style={{ 
                          borderBottom: '1px solid #e2e8f0',
                          cursor: 'pointer',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        onClick={() => navigate(`/admin/assets/${assetId}`)}
                      >
                        <td style={{ padding: '12px' }}>
                          {asset.photos && asset.photos.length > 0 && asset.photos[0].url ? (
                            <img
                              src={getImageUrl(asset.photos[0].url)}
                              alt={asset.description}
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #e2e8f0'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '40px',
                              height: '40px',
                              background: '#f7fafc',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#a0aec0',
                              fontSize: '20px'
                            }}>
                              📦
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', fontWeight: 600, color: '#2d3748', fontSize: '13px' }}>
                          {asset.assetTag || 'N/A'}
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {asset.description || 'N/A'}
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {asset.category || 'N/A'}
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {asset.brand || 'N/A'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <BadgeStatus status={asset.status || 'available'} />
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '13px' }}>
                          {formatDate(asset.assignedDate)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/assets/${assetId}`);
                            }}
                            style={{
                              padding: '6px 12px',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 600
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonDetailPage;
